import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Dialog,
  DialogContent,
  InputAdornment,
  MenuItem,
  IconButton,
  Avatar,
  CircularProgress,
  Tooltip,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaTimes,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaVenusMars,
  FaCamera,
  FaBirthdayCake,
  FaUserFriends,
  FaPhoneSquare,
} from "react-icons/fa";
import { MdSave } from "react-icons/md";
import { LockOutlined } from "@mui/icons-material";

const EASE = [0.22, 1, 0.36, 1];

const TABS = [
  { id: "profile", label: "Profile" },
  { id: "contact", label: "Contact" },
  { id: "guardian", label: "Guardian" },
];

/* ── Shared label ──────────────────────────── */
const FieldLabel = ({ children, required }) => (
  <Typography
    sx={{
      fontSize: "0.67rem",
      fontWeight: 700,
      color: "#94a3b8",
      textTransform: "uppercase",
      letterSpacing: "0.09em",
      mb: 0.55,
      display: "flex",
      alignItems: "center",
      gap: 0.4,
    }}
  >
    {children}
    {required && (
      <Box component="span" sx={{ color: "#f43f5e", fontSize: "0.65rem" }}>
        *
      </Box>
    )}
  </Typography>
);

/* ── Shared input style ────────────────────── */
const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    fontSize: "0.84rem",
    color: "#0f172a",
    background: "#f8fafc",
    transition: "all 0.18s",
    "& fieldset": { borderColor: "#edf0f7" },
    "&:hover fieldset": { borderColor: "#93c5fd" },
    "&.Mui-focused fieldset": { borderColor: "#0288D1", borderWidth: 1.5 },
    "&.Mui-focused": {
      background: "#fff",
      boxShadow: "0 0 0 3px rgba(2,136,209,0.07)",
    },
  },
  "& input::placeholder, & textarea::placeholder": {
    color: "#c8d3df",
    opacity: 1,
    fontSize: "0.82rem",
  },
  "& .MuiSelect-select": { fontSize: "0.84rem" },
};

const adorn = (active) => (active ? "#0288D1" : "#c8d3df");

/* ── Avatar colour map ─────────────────────── */
const AVATAR_COLORS = [
  ["#0284c7", "#38bdf8"],
  ["#7c3aed", "#c4b5fd"],
  ["#b45309", "#fcd34d"],
  ["#047857", "#6ee7b7"],
  ["#be185d", "#f9a8d4"],
  ["#0e7490", "#67e8f9"],
];

/* ════════════════════════════════════════════
   TAB PANELS
════════════════════════════════════════════ */
const ProfileTab = ({
  form,
  setForm,
  preview,
  setPreview,
  saving,
  focused,
  setFocused,
  isEdit,
}) => {
  const nameInitials = form.name
    ?.split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const [c1, c2] =
    AVATAR_COLORS[
      form.name ? form.name.charCodeAt(0) % AVATAR_COLORS.length : 0
    ];

  return (
    <Stack spacing={2}>
      {/* Avatar upload */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2.5, mb: 0.5 }}>
        <Box sx={{ position: "relative", flexShrink: 0 }}>
          <Avatar
            src={preview}
            sx={{
              width: 58,
              height: 58,
              fontSize: "1rem",
              fontWeight: 800,
              background: preview
                ? "transparent"
                : `linear-gradient(135deg, ${c1}, ${c2})`,
              border: "3px solid #fff",
              boxShadow: "0 3px 14px rgba(2,136,209,0.2)",
              color: "#fff",
            }}
          >
            {!preview && (nameInitials || <FaUser size={18} />)}
          </Avatar>

          <Tooltip title="Upload photo" placement="top" arrow>
            <Box
              component="label"
              sx={{
                position: "absolute",
                inset: 0,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                background: "transparent",
                transition: "background 0.2s",
                "& .cam-icon": { opacity: 0, transition: "opacity 0.2s" },
                "&:hover": { background: "rgba(0,0,0,0.4)" },
                "&:hover .cam-icon": { opacity: 1 },
              }}
            >
              <FaCamera className="cam-icon" size={14} color="#fff" />
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setForm({ ...form, passport: file });
                    setPreview(URL.createObjectURL(file));
                  }
                }}
              />
            </Box>
          </Tooltip>

          {preview && (
            <Box
              sx={{
                position: "absolute",
                bottom: 2,
                right: 2,
                width: 12,
                height: 12,
                borderRadius: "50%",
                background: "#22c55e",
                border: "2px solid #fff",
              }}
            />
          )}
        </Box>

        <Box>
          <Typography
            sx={{
              fontSize: "0.82rem",
              fontWeight: 700,
              color: "#0f172a",
              lineHeight: 1.2,
            }}
          >
            {form.name || "Student Name"}
          </Typography>
          <Typography sx={{ fontSize: "0.69rem", color: "#94a3b8", mt: 0.2 }}>
            {isEdit ? "Update student photo" : "Add a passport photo"}
          </Typography>
        </Box>
      </Box>

      {/* Name */}
      <Box>
        <FieldLabel required>Full Name</FieldLabel>
        <TextField
          fullWidth
          placeholder="e.g. Chisom Adaeze"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          onFocus={() => setFocused("name")}
          onBlur={() => setFocused(null)}
          disabled={saving}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FaUser size={12} color={adorn(focused === "name")} />
              </InputAdornment>
            ),
          }}
          sx={inputSx}
        />
      </Box>

      {/* DOB + Gender */}
      <Stack direction="row" spacing={1.5}>
        <Box sx={{ flex: 1 }}>
          <FieldLabel>Date of Birth</FieldLabel>
          <TextField
            fullWidth
            type="date"
            value={form.date_of_birth}
            onChange={(e) =>
              setForm({ ...form, date_of_birth: e.target.value })
            }
            disabled={saving}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FaBirthdayCake size={12} color="#c8d3df" />
                </InputAdornment>
              ),
            }}
            sx={inputSx}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <FieldLabel>Gender</FieldLabel>
          <TextField
            select
            fullWidth
            value={form.gender}
            onChange={(e) => setForm({ ...form, gender: e.target.value })}
            disabled={saving}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FaVenusMars size={12} color="#c8d3df" />
                </InputAdornment>
              ),
            }}
            sx={inputSx}
          >
            <MenuItem value="">Select gender</MenuItem>
            <MenuItem value="Male">Male</MenuItem>
            <MenuItem value="Female">Female</MenuItem>
          </TextField>
        </Box>
      </Stack>
    </Stack>
  );
};

const ContactTab = ({ form, setForm, saving, focused, setFocused }) => (
  <Stack spacing={2}>
    <Box>
      <FieldLabel>Email Address</FieldLabel>
      <TextField
        fullWidth
        placeholder="student@school.edu"
        value={form.email}
        onChange={(e) => setForm({ ...form, email: e.target.value })}
        onFocus={() => setFocused("email")}
        onBlur={() => setFocused(null)}
        disabled={saving}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <FaEnvelope size={12} color={adorn(focused === "email")} />
            </InputAdornment>
          ),
        }}
        sx={inputSx}
      />
    </Box>

    <Box>
      <FieldLabel>Phone Number</FieldLabel>
      <TextField
        fullWidth
        placeholder="+234 800 000 0000"
        value={form.phone}
        onChange={(e) => setForm({ ...form, phone: e.target.value })}
        onFocus={() => setFocused("phone")}
        onBlur={() => setFocused(null)}
        disabled={saving}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <FaPhone size={12} color={adorn(focused === "phone")} />
            </InputAdornment>
          ),
        }}
        sx={inputSx}
      />
    </Box>

    {/* Info notice */}
    <Box
      sx={{
        px: 2,
        py: 1.2,
        borderRadius: 2,
        background: "rgba(2,136,209,0.04)",
        border: "1px solid rgba(2,136,209,0.12)",
      }}
    >
      <Typography
        sx={{ fontSize: "0.7rem", color: "#0369a1", lineHeight: 1.55 }}
      >
        Contact details are used to reach the student directly. These are
        optional but recommended for communication.
      </Typography>
    </Box>
  </Stack>
);

const GuardianTab = ({ form, setForm, saving, focused, setFocused }) => (
  <Stack spacing={2}>
    <Box>
      <FieldLabel>Guardian Name</FieldLabel>
      <TextField
        fullWidth
        placeholder="e.g. Mrs. Ngozi Adaeze"
        value={form.parent_name}
        onChange={(e) => setForm({ ...form, parent_name: e.target.value })}
        onFocus={() => setFocused("pname")}
        onBlur={() => setFocused(null)}
        disabled={saving}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <FaUserFriends size={12} color={adorn(focused === "pname")} />
            </InputAdornment>
          ),
        }}
        sx={inputSx}
      />
    </Box>

    <Box>
      <FieldLabel>Guardian Phone</FieldLabel>
      <TextField
        fullWidth
        placeholder="+234 800 000 0000"
        value={form.parent_phone}
        onChange={(e) => setForm({ ...form, parent_phone: e.target.value })}
        onFocus={() => setFocused("pphone")}
        onBlur={() => setFocused(null)}
        disabled={saving}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <FaPhoneSquare size={12} color={adorn(focused === "pphone")} />
            </InputAdornment>
          ),
        }}
        sx={inputSx}
      />
    </Box>

    <Box
      sx={{
        px: 2,
        py: 1.2,
        borderRadius: 2,
        background: "rgba(251,191,36,0.05)",
        border: "1px solid rgba(251,191,36,0.18)",
      }}
    >
      <Typography
        sx={{ fontSize: "0.7rem", color: "#92400e", lineHeight: 1.55 }}
      >
        Guardian contact is used for emergency notifications and parent-teacher
        communications.
      </Typography>
    </Box>
  </Stack>
);

/* ════════════════════════════════════════════
   MAIN MODAL
════════════════════════════════════════════ */
const StudentModal = ({
  open,
  onClose,
  onSubmit,
  initialData,
  loading,
  access,
  setAccess,
  isDisabled,
}) => {
  const [form, setForm] = useState({
    name: "",
    gender: "",
    date_of_birth: "",
    phone: "",
    email: "",
    parent_name: "",
    parent_phone: "",
    passport: null,
  });
  const [preview, setPreview] = useState(null);
  const [activeTab, setActiveTab] = useState("profile");
  const [focused, setFocused] = useState(null);

  const isEdit = !!initialData;

  useEffect(() => {
    if (open) {
      if (initialData) {
        setForm({
          ...initialData,
          date_of_birth: initialData.date_of_birth
            ? initialData.date_of_birth.split("T")[0]
            : "",
          passport: null,
        });
        setPreview(initialData.passport || null);
      } else {
        setForm({
          name: "",
          gender: "",
          date_of_birth: "",
          phone: "",
          email: "",
          parent_name: "",
          parent_phone: "",
          passport: null,
        });
        setPreview(null);
      }
      setActiveTab("profile");
      setFocused(null);
    }
  }, [initialData, open]);

  const handleSubmit = () => {
    const fd = new FormData();
    fd.append("teacher_code", localStorage.getItem("teacher_code") || "");
    Object.keys(form).forEach((key) => {
      if (key === "passport") {
        if (form[key] instanceof File) fd.append(key, form[key]);
      } else if (form[key] !== null && form[key] !== undefined) {
        fd.append(key, form[key]);
      }
    });
    onSubmit(fd);
  };

  const initials = form.name
    ?.split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const [c1, c2] =
    AVATAR_COLORS[
      form.name ? form.name.charCodeAt(0) % AVATAR_COLORS.length : 0
    ];

  const genderBadgeColor =
    form.gender === "Male"
      ? {
          bg: "rgba(14,165,233,0.22)",
          border: "rgba(14,165,233,0.35)",
          text: "#7dd3fc",
        }
      : form.gender === "Female"
      ? {
          bg: "rgba(244,114,182,0.2)",
          border: "rgba(244,114,182,0.32)",
          text: "#f9a8d4",
        }
      : null;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 0,
          background: "transparent",
          boxShadow: "none",
          overflow: "visible",
        },
      }}
      BackdropProps={{
        sx: {
          backdropFilter: "blur(12px)",
          background: "rgba(8,16,36,0.6)",
        },
      }}
    >
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 28, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 18, scale: 0.97 }}
            transition={{ duration: 0.4, ease: EASE }}
          >
            <DialogContent sx={{ p: 0, overflow: "visible" }}>
              <Box
                sx={{
                  background: "#fff",
                  borderRadius: "22px",
                  border: "1px solid #e4eaf4",
                  boxShadow:
                    "0 40px 100px rgba(2,50,120,0.16), 0 8px 24px rgba(0,0,0,0.07)",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                {/* ── Banner ── */}
                <Box
                  sx={{
                    height: 96,
                    background:
                      "linear-gradient(135deg, #09122c 0%, #0d2448 45%, #0b3565 100%)",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {/* Decorative rings */}
                  {[
                    { size: 120, top: -40, left: -20, opacity: 0.05 },
                    { size: 80, top: 10, right: 40, opacity: 0.06 },
                    { size: 60, bottom: -20, right: 100, opacity: 0.07 },
                    { size: 44, top: 8, left: 80, opacity: 0.05 },
                  ].map((r, i) => (
                    <Box
                      key={i}
                      sx={{
                        position: "absolute",
                        top: r.top,
                        left: r.left,
                        right: r.right,
                        bottom: r.bottom,
                        width: r.size,
                        height: r.size,
                        borderRadius: "50%",
                        border: `1px solid rgba(255,255,255,${
                          r.opacity * 2.5
                        })`,
                        background: `radial-gradient(circle, rgba(255,255,255,${r.opacity}) 0%, transparent 70%)`,
                      }}
                    />
                  ))}

                  {/* Name + meta in banner */}
                  <Box sx={{ position: "absolute", bottom: 12, left: 104 }}>
                    <Typography
                      sx={{
                        fontSize: "0.9rem",
                        fontWeight: 800,
                        color: "rgba(255,255,255,0.95)",
                        textTransform: "capitalize",
                        lineHeight: 1.2,
                        letterSpacing: "-0.3px",
                      }}
                    >
                      {form.name || (isEdit ? "Student" : "New Student")}
                    </Typography>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.8}
                      sx={{ mt: 0.3 }}
                    >
                      <Box
                        sx={{
                          px: 0.9,
                          py: 0.15,
                          borderRadius: 99,
                          background: genderBadgeColor
                            ? genderBadgeColor.bg
                            : "rgba(255,255,255,0.1)",
                          border: `1px solid ${
                            genderBadgeColor
                              ? genderBadgeColor.border
                              : "rgba(255,255,255,0.15)"
                          }`,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            fontWeight: 700,
                            color: genderBadgeColor
                              ? genderBadgeColor.text
                              : "rgba(255,255,255,0.45)",
                            letterSpacing: "0.06em",
                            textTransform: "uppercase",
                          }}
                        >
                          {form.gender || "Student"}
                        </Typography>
                      </Box>
                      {initialData?.student_code && (
                        <Typography
                          sx={{
                            fontSize: "0.62rem",
                            color: "rgba(255,255,255,0.35)",
                            fontFamily: "monospace",
                          }}
                        >
                          · {initialData.student_code}
                        </Typography>
                      )}
                    </Stack>
                  </Box>

                  {/* Close btn */}
                  <IconButton
                    size="small"
                    onClick={onClose}
                    disabled={loading}
                    sx={{
                      position: "absolute",
                      top: 10,
                      right: 10,
                      color: "rgba(255,255,255,0.45)",
                      width: 28,
                      height: 28,
                      transition: "all 0.18s",
                      "&:hover": {
                        background: "rgba(255,255,255,0.1)",
                        color: "rgba(255,255,255,0.9)",
                      },
                    }}
                  >
                    <FaTimes size={11} />
                  </IconButton>
                </Box>

                {/* ── Floating avatar ── */}
                <Box
                  sx={{
                    px: 3,
                    mt: "-32px",
                    position: "relative",
                    zIndex: 2,
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ position: "relative" }}>
                    <Avatar
                      src={preview}
                      sx={{
                        width: 64,
                        height: 64,
                        fontSize: "1.15rem",
                        fontWeight: 800,
                        background: preview
                          ? "transparent"
                          : `linear-gradient(135deg, ${c1}, ${c2})`,
                        border: "3.5px solid #fff",
                        boxShadow: "0 4px 18px rgba(2,136,209,0.25)",
                        color: "#fff",
                        letterSpacing: "-0.5px",
                      }}
                    >
                      {!preview && (initials || <FaUser size={20} />)}
                    </Avatar>

                    <Tooltip title="Upload photo" placement="top" arrow>
                      <Box
                        component="label"
                        sx={{
                          position: "absolute",
                          inset: 0,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          background: "transparent",
                          transition: "background 0.2s",
                          "& .cam-icon": {
                            opacity: 0,
                            transition: "opacity 0.2s",
                          },
                          "&:hover": { background: "rgba(0,0,0,0.4)" },
                          "&:hover .cam-icon": { opacity: 1 },
                        }}
                      >
                        <FaCamera className="cam-icon" size={14} color="#fff" />
                        <input
                          type="file"
                          hidden
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setForm({ ...form, passport: file });
                              setPreview(URL.createObjectURL(file));
                            }
                          }}
                        />
                      </Box>
                    </Tooltip>

                    {preview && (
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: 3,
                          right: 3,
                          width: 13,
                          height: 13,
                          borderRadius: "50%",
                          background: "#22c55e",
                          border: "2.5px solid #fff",
                          boxShadow: "0 0 0 2px rgba(34,197,94,0.2)",
                        }}
                      />
                    )}
                  </Box>

                  {/* Mode badge */}
                  <Box sx={{ mt: 1 }}>
                    <Typography
                      sx={{
                        fontSize: "0.63rem",
                        fontWeight: 700,
                        color: "#94a3b8",
                        textTransform: "uppercase",
                        letterSpacing: "0.1em",
                      }}
                    >
                      {isEdit ? "Edit Student" : "New Student"}
                    </Typography>

                    <FormControlLabel
                      control={
                        <Switch
                          checked={access}
                          onChange={(e) => setAccess(e.target.checked)}
                          color="success"
                        />
                      }
                      label={access ? "Access Enabled" : "Access Disabled"}
                      sx={{fontSize:"0.8rem"}}
                    />
                  </Box>
                </Box>

                {/* ── Tab bar ── */}
                <Box sx={{ px: 3, pt: 1.8 }}>
                  <Stack
                    direction="row"
                    sx={{ borderBottom: "1px solid #f0f4fa" }}
                  >
                    {TABS.map((tab) => {
                      const active = activeTab === tab.id;
                      return (
                        <Box
                          key={tab.id}
                          onClick={() => setActiveTab(tab.id)}
                          sx={{
                            px: 1.6,
                            py: 0.9,
                            cursor: "pointer",
                            position: "relative",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.75rem",
                              fontWeight: active ? 700 : 500,
                              color: active ? "#0288D1" : "#94a3b8",
                              transition: "all 0.16s",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {tab.label}
                          </Typography>
                          {active && (
                            <Box
                              sx={{
                                position: "absolute",
                                bottom: -1,
                                left: 0,
                                right: 0,
                                height: 2,
                                borderRadius: "2px 2px 0 0",
                                background:
                                  "linear-gradient(90deg, #0288D1, #38bdf8)",
                              }}
                            />
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>

                {/* ── Tab content ── */}
                {isDisabled ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mt: 1,
                      px: 1.5,
                      py: 1,
                      borderRadius: 1,
                      background: "rgba(234,179,8,0.08)",
                      border: "0.5px solid rgba(234,179,8,0.4)",
                    }}
                  >
                    <LockOutlined
                      sx={{
                        fontSize: 15,
                        color: "warning.main",
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "0.72rem",
                        color: "warning.dark",
                        lineHeight: 1.5,
                      }}
                    >
                      Access is disabled. Turn on the toggle above to edit this
                      student's profile.
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ px: 3, pt: 2.5, pb: 0, minHeight: 200 }}>
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, x: 8 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -8 }}
                        transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
                      >
                        {activeTab === "profile" && (
                          <ProfileTab
                            form={form}
                            setForm={setForm}
                            preview={preview}
                            setPreview={setPreview}
                            saving={loading}
                            focused={focused}
                            setFocused={setFocused}
                            isEdit={isEdit}
                          />
                        )}
                        {activeTab === "contact" && (
                          <ContactTab
                            form={form}
                            setForm={setForm}
                            saving={loading}
                            focused={focused}
                            setFocused={setFocused}
                          />
                        )}
                        {activeTab === "guardian" && (
                          <GuardianTab
                            form={form}
                            setForm={setForm}
                            saving={loading}
                            focused={focused}
                            setFocused={setFocused}
                          />
                        )}
                      </motion.div>
                    </AnimatePresence>
                  </Box>
                )}

                {/* ── Footer ── */}
                <Box
                  sx={{
                    px: 3,
                    pt: 2,
                    pb: 3,
                    borderTop: "1px solid #f0f4fa",
                    mt: 2.5,
                  }}
                >
                  <Stack direction="row" spacing={1.2}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={onClose}
                      disabled={loading}
                      sx={{
                        py: 1.15,
                        borderRadius: 2,
                        fontWeight: 700,
                        fontSize: "0.82rem",
                        textTransform: "none",
                        borderColor: "#e4eaf4",
                        color: "#64748b",
                        "&:hover": {
                          borderColor: "#94a3b8",
                          background: "#f8fafc",
                          color: "#475569",
                        },
                      }}
                    >
                      Discard
                    </Button>

                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={loading || !form.name?.trim()}
                      startIcon={loading ? null : <MdSave size={13} />}
                      sx={{
                        py: 1.15,
                        borderRadius: 2,
                        fontWeight: 800,
                        fontSize: "0.82rem",
                        textTransform: "none",
                        background:
                          "linear-gradient(135deg, #09122c 0%, #0d3060 100%)",
                        boxShadow: "0 4px 16px rgba(9,18,44,0.28)",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #0d1e42 0%, #0f3d78 100%)",
                          boxShadow: "0 6px 20px rgba(9,18,44,0.38)",
                        },
                        "&:disabled": {
                          background: "#e4eaf4",
                          color: "#94a3b8",
                          boxShadow: "none",
                        },
                      }}
                    >
                      {loading ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <CircularProgress size={13} sx={{ color: "#fff" }} />
                          <span>{isEdit ? "Saving…" : "Creating…"}</span>
                        </Stack>
                      ) : isEdit ? (
                        "Save Changes"
                      ) : (
                        "Register Student"
                      )}
                    </Button>
                  </Stack>
                </Box>
              </Box>
            </DialogContent>
          </motion.div>
        )}
      </AnimatePresence>
    </Dialog>
  );
};

export default StudentModal;
